import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAppStore } from '../store';
import { subscribeToReadings, subscribeToDeviceChanges, fetchReadingsHistory } from '../services/dataSource';
import type { SensorReading, DeviceStatus } from '../types';
import { connectionLog } from '../services/connectionLog';

interface SensorDataContextType {
  readings: SensorReading[];
  current: SensorReading | null;
  outdoorCurrent: SensorReading | null;
  devices: DeviceStatus[];
  insideHistory: SensorReading[];
  outsideHistory: SensorReading[];
  loadingHistory: boolean;
  refreshHistory: () => Promise<void>;
}

export const SensorDataContext = createContext<SensorDataContextType | undefined>(undefined);

export const useSensorData = () => {
  const context = useContext(SensorDataContext);
  if (!context) {
    throw new Error('useSensorData must be used within a SensorDataProvider');
  }
  return context;
};

interface SensorDataProviderProps {
  children: React.ReactNode;
}

export const SensorDataProvider: React.FC<SensorDataProviderProps> = ({ children }) => {
  // Granular selectors — only re-renders this provider when its own slice changes
  const activeTentId = useAppStore(s => s.activeTentId);
  const readings = useAppStore(s => s.readings);
  const current = useAppStore(s => s.current);
  const outdoorCurrent = useAppStore(s => s.outdoorCurrent);
  const devices = useAppStore(s => s.devices);
  // Actions are stable references in Zustand — safe in dep arrays, no re-subscription
  const setCurrent = useAppStore(s => s.setCurrent);
  const setOutdoorCurrent = useAppStore(s => s.setOutdoorCurrent);
  const setReadings = useAppStore(s => s.setReadings);
  const setLastUpdated = useAppStore(s => s.setLastUpdated);
  const setDevices = useAppStore(s => s.setDevices);

  const [insideHistory, setInsideHistory] = useState<SensorReading[]>([]);
  const [outsideHistory, setOutsideHistory] = useState<SensorReading[]>([]);
  const [loadingHistory, setLoadingHistory] = useState<boolean>(false);

  // Unified history fetching for both tents (deduplicated)
  const refreshHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      connectionLog.info('SensorData', 'Geçmiş veriler yükleniyor (tent-1 & tent-2)...');
      const [insideData, outsideData] = await Promise.all([
        fetchReadingsHistory(24, 'tent-1'),
        fetchReadingsHistory(24, 'tent-2'),
      ]);
      setInsideHistory(insideData);
      setOutsideHistory(outsideData);
      connectionLog.success('SensorData', 'Geçmiş veriler başarıyla yüklendi');
    } catch (err) {
      connectionLog.error('SensorData', `Geçmiş veri yükleme hatası: ${err}`);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  // Fetch history on mount
  useEffect(() => {
    refreshHistory();
  }, [refreshHistory]);

  // Unified single Realtime subscriptions
  useEffect(() => {
    connectionLog.info('SensorData', `Tekil realtime sensör aboneliği başlatılıyor (aktif: ${activeTentId})`);
    const unsubReadings = subscribeToReadings(
      (reading) => {
        if (reading.tentId === activeTentId) {
          setCurrent(reading);
          setReadings((prev: SensorReading[]) => [...prev.slice(-99), reading]);
        } else {
          setOutdoorCurrent(reading);
        }
        setLastUpdated(new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }));

        if (reading.tentId === 'tent-1') {
          setInsideHistory(prev => [...prev.slice(-100), reading]);
        } else if (reading.tentId === 'tent-2') {
          setOutsideHistory(prev => [...prev.slice(-100), reading]);
        }
      },
      (err) => {
        connectionLog.error('SensorData', `Sensör veri akışı hatası: ${err}`);
      }
    );

    const unsubDevices = subscribeToDeviceChanges((device) => {
      setDevices((prev: DeviceStatus[]) => prev.map(d => d.id === device.id ? device : d));
    });

    return () => {
      unsubReadings();
      unsubDevices();
      connectionLog.info('SensorData', 'Tekil realtime abonelikleri sonlandırıldı');
    };
  }, [activeTentId, setCurrent, setOutdoorCurrent, setReadings, setLastUpdated, setDevices]);

  const value: SensorDataContextType = {
    readings,
    current,
    outdoorCurrent,
    devices,
    insideHistory,
    outsideHistory,
    loadingHistory,
    refreshHistory,
  };

  return (
    <SensorDataContext.Provider value={value}>
      {children}
    </SensorDataContext.Provider>
  );
};
